import { z } from "zod";
import { pageQuerySchema } from "./common.ts";

const LEVEL_KEY_RE = /^[a-z0-9-]{2,20}$/;

/** 分销商给自己的商品定价。 */
export const setMarkupRateSchema = z.object({
  /** 加价率，0.15 = 15%。 */
  rate: z.number().min(0).max(100),
  categoryId: z.number().int().positive().optional(),
  productId: z.number().int().positive().optional(),
  note: z.string().max(200).optional(),
});

export const setSkuPriceSchema = z.object({
  skuId: z.number().int().positive(),
  /** 售价，单位分。⚠️ 要过限价检查（默认不得低于进货价）。 */
  price: z.number().int().positive(),
  /**
   * 改的是哪个价。
   * 🔴 `chain`（默认）= 我卖出去的价，下级按它进货、我的客户也付它。
   *    `shop` = **只有平台**有的第二个价：陈总店里卖给消费者多少。
   */
  kind: z.enum(["chain", "shop"]).optional(),
});

export const createOrgSchema = z.object({
  type: z.enum(["supplier", "dealer", "distributor"]),
  parentId: z.number().int().positive(),
  name: z.string().min(2).max(60),
  displayName: z.string().max(60).optional(),
  ownerUserId: z.number().int().positive().optional(),
  contactName: z.string().max(32).optional(),
  contactPhone: z.string().max(20).optional(),
});

export const reparentSchema = z.object({
  newParentId: z.number().int().positive(),
});

export const closeOrgSchema = z.object({
  reason: z.string().min(1, "清退必须填写理由").max(500),
  /** 名下用户怎么安置。默认上挂一级。 */
  breakPolicy: z.enum(["attach_parent", "detach", "manual"]).optional(),
});

export const transferUserSchema = z.object({
  userId: z.number().int().positive(),
  toOrgId: z.number().int().positive(),
  reason: z.string().min(1, "手工转移必须填写理由").max(500),
});

export const withdrawalSchema = z.object({
  amount: z.number().int().positive(),
  payee: z.object({
    kind: z.enum(["bank", "alipay", "wechat"]),
    name: z.string().min(1).max(32),
    account: z.string().min(1).max(64),
    bankName: z.string().max(64).optional(),
  }),
});

export const reviewWithdrawalSchema = z.object({
  reason: z.string().max(500).optional(),
});

export const earningsQuerySchema = pageQuerySchema;

/** 等级定义。 */
export const upsertLevelSchema = z.object({
  key: z.string().regex(LEVEL_KEY_RE, "只能用小写字母、数字和连字号"),
  name: z.string().min(1).max(20),
  rank: z.number().int().min(0).max(999).optional(),
  thresholdGmv: z.number().int().nonnegative().optional(),
  thresholdDirectCount: z.number().int().nonnegative().optional(),
  defaultMarkupRate: z.number().min(0).max(100).optional(),
  description: z.string().max(200).optional(),
  active: z.boolean().optional(),
});

export const setLevelSchema = z.object({
  levelKey: z.string().min(1),
  reason: z.string().min(1, "手工调级必须填写理由").max(500),
});

/** 收费策略。 */
export const feePolicySchema = z.object({
  kind: z.enum([
    "margin",
    "periodic",
    "gmv_commission",
    "profit_commission",
    "per_order",
    "deposit",
  ]),
  scope: z.enum(["platform", "org_type", "level", "org"]),
  scopeValue: z.string().max(50).default(""),
  enabled: z.boolean().default(false),
  value: z.number().min(0),
  period: z.enum(["monthly", "quarterly", "yearly"]).optional(),
  /**
   * 🔴 合规护栏：周期费与保证金默认只对经销商开放。
   *    对线上零门槛注册的分销商收入门费会踩传销认定的红线
   *    （「要求被发展人员交纳费用作为取得加入资格的条件」）。
   */
  applicableOrgTypes: z.array(z.enum(["dealer", "distributor"])).min(1),
  collectorOrgId: z.number().int().positive().nullable().optional(),
  minRetainRate: z.number().min(0).max(1).default(0.5),
  directOnly: z.boolean().default(true),
  note: z.string().max(200).optional(),
});

/**
 * 改抽成策略 —— **不能用 `feePolicySchema.partial()`**。
 *
 * 🔴 zod 的 `.partial()` 只把字段变成可选，**带 `.default()` 的字段
 *    在 parse 时仍会填充默认值**。于是 PATCH 只传 `{ enabled: true }`，
 *    解析出来却是 `{ enabled: true, scopeValue: "", minRetainRate: 0.5,
 *    directOnly: true }` —— 点一下开关，把规则的作用范围、保底线、
 *    是否只对直接下级**全部重置**了。
 *
 *    这些字段直接决定每一笔订单的钱怎么抽，被悄悄改掉是资金事故；
 *    而且请求成功、类型也对，只有比对数据才看得出来（真机点开关时发现的）。
 *
 * ⚠️ 所以这里每个字段都是显式 `.optional()` 且**一个 default 都不带**：
 *    没传就是没传，服务端不动那一列。
 */
export const patchFeePolicySchema = z.object({
  kind: z
    .enum(["margin", "periodic", "gmv_commission", "profit_commission", "per_order", "deposit"])
    .optional(),
  scope: z.enum(["platform", "org_type", "level", "org"]).optional(),
  scopeValue: z.string().max(50).optional(),
  enabled: z.boolean().optional(),
  value: z.number().min(0).optional(),
  period: z.enum(["monthly", "quarterly", "yearly"]).optional(),
  applicableOrgTypes: z.array(z.enum(["dealer", "distributor"])).min(1).optional(),
  collectorOrgId: z.number().int().positive().nullable().optional(),
  minRetainRate: z.number().min(0).max(1).optional(),
  directOnly: z.boolean().optional(),
  note: z.string().max(200).optional(),
});

/**
 * 确认调价提醒（"我知道了"）。
 *
 * ⚠️ 只带 id 列表，**不带 orgId** —— 归属从会话取。
 *    让客户端传 orgId 等于让任何人替别人已读掉倒挂告警。
 */
export const ackPriceNoticesSchema = z.object({
  ids: z.array(z.number().int().positive()).min(1).max(200),
});

/** 签约周期费 / 交保证金：只挑一条策略，金额由策略定，客户端不许传。 */
export const subscribeFeeSchema = z.object({
  policyId: z.number().int().positive(),
});

/**
 * 没收保证金。
 *
 * 🔴 **理由是必填且有下限的。** 这笔钱本来是别人的 ——
 *    填个"1"跟没填一样，纠纷时拿不出任何依据。
 */
export const forfeitDepositSchema = z.object({
  reason: z.string().trim().min(4).max(200),
});

/** 重发订单通知。⚠️ 只带 id，不接受客户端指定收件人 —— 那是发信滥用的入口。 */
export const retryNotificationsSchema = z.object({
  ids: z.array(z.number().int().positive()).min(1).max(200),
});

/**
 * 开后台账号。
 *
 * ⚠️ 绑的是**已注册的会员**（用手机号找）—— 后台不单独设密码，
 *    走会员那套登录（含撞库防护、设备管理）。
 *    真正的隔离是：`admin_users` 里没有这条记录就进不了后台。
 */
export const createAdminSchema = z.object({
  phone: z.string().min(6).max(20),
  name: z.string().min(1).max(30),
  role: z.string().min(1).max(32),
});

/** ⚠️ 显式全 optional 且不带 default —— `.partial()` 会把 default 一起带进来。 */
export const updateAdminSchema = z.object({
  role: z.string().min(1).max(32).optional(),
  name: z.string().min(1).max(30).optional(),
  disabled: z.boolean().optional(),
});

export const upsertRoleSchema = z.object({
  name: z.string().min(1).max(30),
  description: z.string().max(200).optional(),
  /** ⚠️ 服务端会过滤掉不认识的键，前端传多了不会报错但也不会生效。 */
  permissions: z.array(z.string().max(64)).max(100),
  enabled: z.boolean().optional(),
});
